"use client";

import { DynamicLinkButton } from "@/components/common/dynamic-link-button/dynamic-link-button";
import ScreenLayout from "@/components/layout/screen-layout";
import { AnimatedGridPattern } from "@/components/magicui/animated-grid-pattern";
import { cn } from "@/lib/utils";
import React, { useEffect, useState } from "react";

export interface StatItem {
  value: string;
  label: string;
}

type ButtonVariant = "default" | "secondary" | "dark_blue";

interface ReadyToTurnRealityProps {
  heading: string;
  subheading: string;
  button: {
    label: string;
    href: string;
    variant?: ButtonVariant;
  };
  stats: StatItem[];
}

type DotStyle = {
  left: string;
  top: string;
  opacity: number;
  transform: string;
};

const ReadyToTurnRealitySection: React.FC<ReadyToTurnRealityProps> = ({
  heading,
  subheading,
  button,
  stats,
}) => {
  const [dots, setDots] = useState<DotStyle[]>([]);

  useEffect(() => {
    setDots(
      Array.from({ length: 20 }, () => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        opacity: Math.random() * 0.5 + 0.3,
        transform: `scale(${Math.random() * 0.5 + 0.5})`,
      }))
    );
  }, []);

  return (
    <div className="relative">
      <ScreenLayout className="py-10 md:py-16 lg:py-20 relative" >
        <div
          className="relative flex flex-col justify-center items-center text-white space-y-10 md:space-y-12 py-12 md:py-16 lg:py-20 px-8 md:px-12 lg:px-20 rounded-[32px] bg-[radial-gradient(80%_70%_at_50%_-10%,#C4C0FF_0%,#4642A5_15%,#1D1875_35%,#0A0642_65%,#050327_100%)] overflow-hidden shadow-[0_15px_60px_rgba(5,3,39,0.6)]"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-[rgba(196,192,255,0.18)] to-transparent pointer-events-none"></div>
          <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-[#5D59C7] opacity-20 blur-[80px]"></div>
          <div className="absolute -bottom-24 -left-24 w-48 h-48 rounded-full bg-[#B4B0FE] opacity-15 blur-[80px]"></div>
          <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-[#8A87E0] opacity-10 blur-[60px]"></div>

          <AnimatedGridPattern
            numSquares={48}
            maxOpacity={0.06}
            duration={5}
            repeatDelay={0}
            className={cn(
              "absolute inset-x-0 inset-y-0 h-full w-full overflow-hidden rounded-[32px]",
            )}
          />

          {/* Subtle dots */}
          <div className="absolute inset-0 overflow-hidden opacity-20">
            {dots.map((dot, i) => (
              <div
                key={i}
                className="absolute h-1 w-1 rounded-full bg-white"
                style={dot}
              />
            ))}
          </div>

          {/* Heading */}
          <h2
            className="text-center text-3xl sm:text-4xl md:text-5xl lg:text-[56px] font-medium max-w-4xl z-10 bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-[#E2E1FF] leading-tight"
          >
            {heading}
          </h2>

          {/* Subheading */}
          <div
            className="w-full max-w-3xl mx-auto z-10"
          >
            <p className="text-base md:text-lg font-normal text-center text-white/90 leading-relaxed">
              {subheading}
            </p>
          </div>

          {/* Stats */}
          <div
            className="flex flex-wrap justify-center items-center gap-x-8 md:gap-x-10 gap-y-8 w-full max-w-5xl mx-auto z-10"
          >
            {stats.map((stat, i) => (
              <div
                key={i}
                className="group relative cursor-pointer 
                bg-[linear-gradient(135deg,_rgba(255,255,255,0.06)_0%,_rgba(255,255,255,0.02)_100%)]
                shadow-[0px_15px_35px_rgba(0,0,0,0.2),_inset_0_0_0_1px_rgba(255,255,255,0.08)]
                backdrop-blur-[15px]
                rounded-2xl
                w-[160px] md:w-[180px] lg:w-[220px]
                flex flex-col justify-center items-center
                py-6 px-4 md:py-7 md:px-6
                overflow-hidden
                transition-all duration-300 hover:scale-102 hover:shadow-[0px_20px_40px_rgba(0,0,0,0.3)]"
              >
                {/* Gradient overlay that shows on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-[rgba(196,192,255,0.15)] via-[rgba(180,176,254,0.08)] to-transparent transition-opacity duration-500"></div>

                {/* Decorative corner accents */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-white/10 to-transparent opacity-50"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-white/5 to-transparent opacity-40"></div>

                {/* Value */}
                <div className="relative mb-3 mt-0">
                  <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-[#F0F0FF] to-[#E2E1FF] group-hover:from-white group-hover:to-white transition-colors duration-300">
                    {stat.value}
                  </h1>
                </div>

                {/* Label */}
                <div className="relative">
                  <h3 className="uppercase text-sm md:text-base font-medium tracking-wide text-center text-white/70 group-hover:text-white/95 transition-colors duration-300">
                    {stat.label}
                  </h3>
                </div>

                {/* Border effect */}
                <div className="absolute inset-0 rounded-2xl border border-white/0 group-hover:border-white/10 transition-colors duration-500"></div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div
            className="mt-6 md:mt-8 z-10"
          >
            <DynamicLinkButton
              value={button.label}
              href={button.href}
              variant={button.variant ?? "secondary"}
              className="shadow-lg hover:shadow-xl transition-all duration-300 transform hover:translate-y-[-2px]"
            />
          </div>
        </div>
      </ScreenLayout>
    </div>
  );
};

export default ReadyToTurnRealitySection;
